<script lang="ts" setup>
import { useMutation, useQuery } from "@tanstack/vue-query";
import {
    NButton,
    NCard,
    NDropdown,
    NModal,
    NSelect,
    NSpin,
    NTable,
    NTag,
    NTooltip,
    useMessage,
} from "naive-ui";
import { computed, reactive, ref } from "vue";
import { useOrganization } from "../../hooks/useOrganization";
import { Property } from "../../models/contador";
import { searchPropertiesV2 } from "../../services/contador/properties.service";
import type { PropertiesSearchRequest } from "../../models/search";
import {
    createContact,
    deactivateUser as deactivateUserService,
    inviteContact,
    reactivateUser as reactivateUserService,
    revokeInvitation,
    updateContact,
} from "../../services/user";
import { ProperText } from "@propertechnologies/ajiaco";

const message = useMessage();
const { organization, loadingOrganization, reloadOrganization } = useOrganization();

const state = reactive({
    newUserEmail: "",
});

// Associate Properties modal state
const showAssociateModal = ref(false);
const selectedContact = ref<any>(null);
const selectedProperties = ref<string[]>([]);

// Consolidated data refetch function
const refetchData = () => {
    reloadOrganization();
};

const { mutate: inviteUser, isPending: isInviting } = useMutation({
    mutationFn: async (email: string) => {
        // Check if contact already exists with this email
        const existingContact = organization.value?.contacts?.find(
            (c) => c.email.toLowerCase() === email.toLowerCase(),
        );

        if (existingContact) {
            // Contact exists, just invite them
            await inviteContact(existingContact.id);
        } else {
            // Create contact first, then invite
            const newContact = await createContact(email);
            await inviteContact(newContact.id);
        }
    },
    onSuccess: () => {
        state.newUserEmail = "";
        refetchData();
        message.success("User invited successfully");
    },
    onError: (error: Error) => {
        message.error(`Failed to invite user: ${error.message}`);
    },
});

const { mutate: revokeInvitationMutation, isPending: isRevoking } = useMutation({
    mutationFn: (contactId: string) => revokeInvitation(contactId),
    onSuccess: () => {
        refetchData();
        message.success("Invitation revoked successfully");
    },
    onError: (error: Error) => {
        message.error(`Failed to revoke invitation: ${error.message}`);
    },
});

const { mutate: deactivateUserMutation, isPending: isDeactivating } = useMutation({
    mutationFn: (contactId: string) => deactivateUserService(contactId),
    onSuccess: () => {
        refetchData();
        message.success("User deactivated successfully");
    },
    onError: (error: Error) => {
        message.error(`Failed to deactivate user: ${error.message}`);
    },
});

const { mutate: reactivateUserMutation, isPending: isReactivating } = useMutation({
    mutationFn: (contactId: string) => reactivateUserService(contactId),
    onSuccess: () => {
        refetchData();
        message.success("User reactivated successfully");
    },
    onError: (error: Error) => {
        message.error(`Failed to reactivate user: ${error.message}`);
    },
});

const sortedContacts = computed(() => {
    return (
        organization.value?.contacts
            ?.slice()
            .sort((a, b) => new Date(b.created_at).getTime() - new Date(a.created_at).getTime()) ||
        []
    );
});

const handleInvite = () => {
    if (state.newUserEmail.trim()) {
        inviteUser(state.newUserEmail.trim());
    }
};

const isAnyActionPending = computed(
    () => isInviting.value || isRevoking.value || isDeactivating.value || isReactivating.value,
);

// Load properties for the organization
const { data: properties, isLoading: isLoadingProperties } = useQuery({
    queryKey: ["properties-v2", computed(() => organization.value?.id)],
    queryFn: async () => {
        if (!organization.value?.id) {
            return { items: [], total: 0, offset: 0, limit: 0 };
        }
        const pageLimit = 5000;
        let offset = 0;
        let allItems: Property[] = [];

        const firstPayload: PropertiesSearchRequest = {
            where: {},
            limit: pageLimit,
            offset,
        } as any;
        const firstResponse = await searchPropertiesV2(firstPayload);
        const firstItems = firstResponse?.items ?? [];
        const total = firstResponse?.total ?? firstItems.length;

        if (firstItems.length > 0) {
            allItems = allItems.concat(firstItems as Property[]);
            offset += pageLimit;
        }

        while (allItems.length < total) {
            const payload: PropertiesSearchRequest = {
                where: {},
                limit: pageLimit,
                offset,
            } as any;
            const response = await searchPropertiesV2(payload);
            const items = response?.items ?? [];

            if (items.length === 0) {
                break;
            }

            allItems = allItems.concat(items as Property[]);
            offset += pageLimit;
        }

        return {
            items: allItems,
            total: total || allItems.length,
            offset: 0,
            limit: allItems.length,
        };
    },
    enabled: computed(() => !!organization.value?.id),
});

const propertyOptions = computed(() => {
    return (
        properties.value?.items?.map((property: Property) => ({
            label: property.name,
            value: property.id,
        })) || []
    );
});

// Create a lookup map for property names
const propertyNameById = computed(() => {
    const map: Record<string, string> = {};
    properties.value?.items?.forEach((property: Property) => {
        map[property.id] = property.name;
    });
    return map;
});

// Update contact properties mutation
const { mutate: updateContactProperties, isPending: isUpdatingProperties } = useMutation({
    mutationFn: async () => {
        if (!selectedContact.value) return;

        await updateContact(selectedContact.value.id, {
            ...selectedContact.value,
            properties_ids: selectedProperties.value,
        });
    },
    onSuccess: () => {
        reloadOrganization();
        closeAssociateModal();
        message.success("Properties updated successfully");
    },
    onError: (error: Error) => {
        message.error(`Failed to update properties: ${error.message}`);
    },
});

// Function to get property names from IDs
const getPropertyNames = (contact: any): string[] => {
    // Check both possible field names: propertiesIds (camelCase) and properties_ids (snake_case)
    const propertyIds = contact?.propertiesIds || contact?.properties_ids;
    if (!propertyIds || propertyIds.length === 0) return [];
    return propertyIds.map((id: string) => propertyNameById.value[id] || id);
};

// Function to get property count for display
const getPropertyCount = (contact: any): number => {
    const propertyIds = contact?.propertiesIds || contact?.properties_ids;
    return propertyIds?.length || 0;
};

const getActionOptions = (contact: any) => {
    const options = [];

    if (contact.portal_status === "uninvited") {
        options.push({
            label: "Send Invite",
            key: "invite",
            disabled: isAnyActionPending.value,
            props: {
                onClick: () => handleSendInvite(contact),
            },
        });
    }

    if (contact.portal_status === "invited") {
        options.push({
            label: "Revoke Invitation",
            key: "revoke",
            disabled: isAnyActionPending.value,
            props: {
                onClick: () => handleRevokeInvitation(contact),
            },
        });
    }

    if (contact.portal_status === "active") {
        options.push({
            label: "Deactivate User",
            key: "deactivate",
            disabled: isAnyActionPending.value,
            props: {
                onClick: () => handleDeactivateUser(contact),
            },
        });
    }

    if (contact.portal_status === "inactive") {
        options.push({
            label: "Reactivate User",
            key: "reactivate",
            disabled: isAnyActionPending.value,
            props: {
                onClick: () => handleReactivateUser(contact),
            },
        });
    }

    options.push({
        label: "Associate Properties",
        key: "associate",
        disabled: false,
        props: {
            onClick: () => handleAssociateProperties(contact),
        },
    });

    return options;
};

const handleSendInvite = (contact: any) => {
    inviteUser(contact.email);
};

const handleRevokeInvitation = (contact: any) => {
    revokeInvitationMutation(contact.id);
};

const handleDeactivateUser = (contact: any) => {
    deactivateUserMutation(contact.id);
};

const handleReactivateUser = (contact: any) => {
    reactivateUserMutation(contact.id);
};

const handleAssociateProperties = (contact: any) => {
    selectedContact.value = contact;
    // Check both possible field names: propertiesIds (camelCase) and properties_ids (snake_case)
    selectedProperties.value = contact.propertiesIds || contact.properties_ids || [];
    showAssociateModal.value = true;
};

const closeAssociateModal = () => {
    showAssociateModal.value = false;
    selectedContact.value = null;
    selectedProperties.value = [];
};

const getStatusTag = (contact: any) => {
    if (isAnyActionPending.value) return { type: "warning", text: "Processing..." };

    switch (contact.portal_status) {
        case "invited":
            return { type: "info", text: "Invited" };
        case "active":
            return { type: "success", text: "Active" };
        case "inactive":
            return { type: "error", text: "Deactivated" };
        case "uninvited":
        default:
            return { type: "default", text: "Not Invited" };
    }
};
</script>

<template>
    <div class="flex flex-col gap-6 overflow-auto">
        <!-- Header Card -->
        <div class="bg-blurple-500 p-2 sm:p-3 md:p-4 rounded-lg">
            <div class="flex flex-col gap-6">
                <div>
                    <h3 class="text-lg font-semibold">Add invitee emails here.</h3>
                    <p class="text-lg font-semibold text-copy-dim">
                        Once invited, they will receive an email invite to sign in and set their
                        password through.
                    </p>
                </div>

                <div class="flex flex-col sm:flex-row gap-3 w-full">
                    <proper-text
                        :model-value="state.newUserEmail"
                        placeholder="Invitee email here..."
                        class="w-full sm:max-w-[300px]"
                        @update:model-value="(v) => (state.newUserEmail = v as string)"
                    />
                    <button
                        class="button w-full sm:w-auto !py-[9px] !px-4 !h-fit"
                        :disabled="!state.newUserEmail.trim() || isInviting"
                        @click="handleInvite"
                    >
                        {{ isInviting ? "Sending..." : "Send invitation" }}
                    </button>
                </div>
            </div>
        </div>

        <!-- Users Table -->
        <div v-if="loadingOrganization && !organization" class="flex justify-center py-8">
            <n-spin size="medium" />
        </div>
        <div v-else>
            <div class="overflow-x-auto">
                <n-table :single-line="false" class="border border-border-gray">
                    <thead>
                        <tr class="bg-gray-50">
                            <th
                                class="py-3 px-4 text-left font-medium text-gray-900 uppercase text-xs tracking-wide"
                            >
                                NAME
                            </th>
                            <th
                                class="py-3 px-4 text-left font-medium text-gray-900 uppercase text-xs tracking-wide"
                            >
                                EMAIL
                            </th>
                            <th
                                class="py-3 px-4 text-left font-medium text-gray-900 uppercase text-xs tracking-wide"
                            >
                                ASSOCIATED PROPERTIES
                            </th>
                            <th
                                class="py-3 px-4 text-right font-medium text-gray-900 uppercase text-xs tracking-wide"
                            ></th>
                        </tr>
                    </thead>
                    <tbody>
                        <tr
                            v-for="contact in sortedContacts"
                            :key="contact.id"
                            class="border-t border-gray-200 hover:bg-gray-50"
                        >
                            <td class="py-4 px-4 text-sm">
                                <div class="flex justify-between gap-1">
                                    <span>{{ contact.name || contact.email }}</span>
                                    <n-tag
                                        size="small"
                                        :bordered="false"
                                        :round="true"
                                        :type="getStatusTag(contact).type as any"
                                        class="w-fit"
                                    >
                                        {{ getStatusTag(contact).text }}
                                    </n-tag>
                                </div>
                            </td>
                            <td class="py-4 px-4 text-sm text-gray-600">
                                {{ contact.email }}
                            </td>
                            <td class="py-4 px-4 text-sm">
                                <n-tooltip trigger="hover">
                                    <template #trigger>
                                        <n-tag
                                            size="small"
                                            type="info"
                                            :bordered="false"
                                            class="bg-cream-dark text-gray-800"
                                        >
                                            {{ getPropertyCount(contact) }}
                                        </n-tag>
                                    </template>
                                    <div v-if="getPropertyCount(contact) > 0">
                                        <div
                                            v-for="propertyName in getPropertyNames(contact)"
                                            :key="propertyName"
                                        >
                                            {{ propertyName }}
                                        </div>
                                    </div>
                                    <div v-else>No properties assigned</div>
                                </n-tooltip>
                            </td>
                            <td class="py-4 px-4 text-right">
                                <n-dropdown
                                    trigger="click"
                                    :options="getActionOptions(contact)"
                                    :disabled="isAnyActionPending"
                                >
                                    <n-button
                                        text
                                        class="text-gray-500 hover:text-gray-700"
                                        :disabled="isAnyActionPending"
                                    >
                                        <template #icon>
                                            <span class="material-symbols-outlined text-lg"
                                                >more_vert</span
                                            >
                                        </template>
                                    </n-button>
                                </n-dropdown>
                            </td>
                        </tr>
                    </tbody>
                </n-table>
            </div>

            <div v-if="sortedContacts.length === 0" class="text-center py-8 text-gray-500">
                No team members yet. Invite someone to get started!
            </div>
        </div>
    </div>

    <!-- Associate Properties Modal -->
    <n-modal v-model:show="showAssociateModal">
        <n-card style="width: 600px" title="Associate Properties" :bordered="false">
            <template #header-extra>
                <span class="material-symbols-outlined cursor-pointer" @click="closeAssociateModal"
                    >close</span
                >
            </template>

            <div class="mb-6">
                <p class="text-sm text-gray-600 mb-4">
                    {{ selectedContact?.name || selectedContact?.email }} will see open items
                    associated with the following properties, plus any open item not associated with
                    any property.
                </p>

                <div class="space-y-4">
                    <n-select
                        v-model:value="selectedProperties"
                        :options="propertyOptions"
                        multiple
                        filterable
                        placeholder="Select properties..."
                        :loading="isLoadingProperties"
                        class="w-full"
                    />
                </div>
            </div>

            <template #footer>
                <div class="flex justify-end gap-3">
                    <n-button @click="closeAssociateModal">Cancel</n-button>
                    <n-button
                        type="primary"
                        @click="() => updateContactProperties()"
                        :loading="isUpdatingProperties"
                    >
                        Save
                    </n-button>
                </div>
            </template>
        </n-card>
    </n-modal>
</template>

<style scoped>
/* Custom styling for the table and components */
:deep(.n-table th) {
    background-color: #f9fafb;
}

:deep(.n-button--primary-type) {
    background-color: #374151;
    border-color: #374151;
}

:deep(.n-button--primary-type:hover) {
    background-color: #1f2937;
    border-color: #1f2937;
}

:deep(.n-button--primary-type:focus) {
    background-color: #1f2937;
    border-color: #1f2937;
}
</style>
